// src/components/billing/CurrentSubscriptionDetails.jsx
import React from 'react';
import {
  Typography, Button, Skeleton, Box, List, ListItem, ListItemIcon, ListItemText, Alert as MuiAlert,
  CircularProgress, useTheme, Chip, Stack, alpha, Tooltip,
} from '@mui/material';
import {
  CreditCardOutlined as CreditCardIcon, // Outlined variant
  WorkspacePremiumOutlined as PremiumIcon, // For active/trial plans
  AutorenewRounded as AutorenewIcon,
  EventBusyRounded as EventBusyIcon,
  CheckCircleRounded as CheckCircleOutlineIcon,
  ErrorRounded as ErrorOutlineIcon,
  HourglassTopRounded as HourglassEmptyIcon,
  HelpOutlineRounded as HelpOutlineIcon,
  CelebrationRounded as CelebrationIcon,
  ManageAccountsOutlined as ManageAccountsIcon,
  ExploreOutlined as ExploreIcon, // For explore plans
} from '@mui/icons-material';
import SectionCard from './SectionCard';

const formatDate = (dateString, options) => {
  if (!dateString) return 'N/A';
  const defaultOptions = { year: 'numeric', month: 'short', day: 'numeric' }; // Simpler default
  return new Date(dateString).toLocaleDateString(undefined, { ...defaultOptions, ...options });
};

const getSubscriptionStatusPresentation = (status = "unknown", theme) => {
  const baseSx = {fontSize: '1.25rem', verticalAlign: 'middle', mr: 0.5};
  switch (status.toLowerCase()) {
    case 'active':
      return { icon: <PremiumIcon sx={{...baseSx, color: theme.palette.success.main }} />, label: "Active Subscription", color: 'success', variant: 'filled', severity: 'success' };
    case 'trialing':
      return { icon: <CelebrationIcon sx={{...baseSx, color: theme.palette.info.main }} />, label: "Active Trial", color: 'info', variant: 'filled', severity: 'info' };
    case 'past_due':
      return { icon: <ErrorOutlineIcon sx={{...baseSx, color: theme.palette.error.main }} />, label: "Payment Past Due", color: 'error', variant: 'filled', severity: 'error' };
    case 'incomplete':
      return { icon: <ErrorOutlineIcon sx={{...baseSx, color: theme.palette.warning.main }} />, label: "Payment Incomplete", color: 'warning', variant: 'outlined', severity: 'warning' };
    case 'canceled':
      return { icon: <EventBusyIcon sx={{...baseSx, color: theme.palette.text.secondary }} />, label: "Canceled", color: 'default', variant: 'outlined', severity: 'warning'};
    case 'incomplete_expired':
      return { icon: <HourglassEmptyIcon sx={{...baseSx, color: theme.palette.text.disabled }} />, label: "Expired / Incomplete", color: 'default', variant: 'outlined', severity: 'error' };
    default:
      return { icon: <HelpOutlineIcon sx={{...baseSx, color: theme.palette.text.disabled }} />, label: status ? status.replace(/_/g, ' ') : "Unknown Status", color: 'default', variant: 'outlined', severity: 'info' };
  }
};

const CurrentSubscriptionDetails = ({
  subscription,
  isLoading,
  isLoadingCheckout,
  onManageSubscription,
  onActivateFreeTier,
  onExplorePaidPlans,
  isEligibleForFreeTier,
  freeTierPlanId, // Ensure this is the stripe_price_id for the free tier plan
}) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <SectionCard title="Subscription Status" icon={<CreditCardIcon />}>
        <Stack spacing={1.5} sx={{mt:1, alignItems: 'flex-start'}}>
            <Skeleton variant="rectangular" width="70%" height={36} sx={{ borderRadius: '8px' }} />
            <Skeleton variant="text" width="90%" height={20} />
            <Skeleton variant="text" width="80%" height={20} />
            <Skeleton variant="text" width="85%" height={20} />
            <Skeleton variant="rectangular" width="100%" height={52} sx={{mt:1.5, borderRadius: '12px'}} />
        </Stack>
      </SectionCard>
    );
  }

  const isEffectivelyNoActiveSub = !subscription ||
    ['incomplete_expired', 'canceled'].includes(subscription.status?.toLowerCase());
    
  const statusInfo = getSubscriptionStatusPresentation(subscription?.status, theme);

  if (isEffectivelyNoActiveSub) {
    return (
      <SectionCard title="Subscription Status" icon={<CreditCardIcon />} contentSx={{ justifyContent: 'space-between' }}>
        <Box>
            <MuiAlert 
                severity="info" 
                icon={<HelpOutlineIcon />} 
                sx={{ 
                    mb: 2.5, 
                    display: 'flex', 
                    alignItems: 'center', 
                    borderRadius: '12px',
                    backgroundColor: alpha(theme.palette.info.main, 0.08),
                    color: theme.palette.info.darker || theme.palette.info.dark,
                    '& .MuiAlert-icon': { color: theme.palette.info.main }
                }}
            >
                You do not have an active subscription plan.
            </MuiAlert>
        </Box>
        <Stack spacing={1.5} sx={{mt: 'auto'}}> {/* Pushes buttons to bottom */}
            {isEligibleForFreeTier && freeTierPlanId && (
            <Button
                variant="contained"
                color="success"
                onClick={() => freeTierPlanId && onActivateFreeTier(freeTierPlanId)} // Pass ID
                disabled={isLoadingCheckout || !freeTierPlanId}
                fullWidth
                sx={{ py: 1.5, textTransform: 'none', fontWeight: 'bold', fontSize: '0.9375rem', borderRadius: '12px' }}
                startIcon={isLoadingCheckout ? <CircularProgress size={20} color="inherit" /> : <CelebrationIcon />}
            >
                Activate Free Tier (30 Days Free!)
            </Button>
            )}
            <Button
                variant="outlined"
                color="primary"
                onClick={onExplorePaidPlans}
                fullWidth
                sx={{ py: 1.5, textTransform: 'none', fontWeight: 500, fontSize: '0.9375rem', borderRadius: '12px' }}
                startIcon={<ExploreIcon />}
            >
            {isEligibleForFreeTier && freeTierPlanId ? "Or, Explore Other Plans" : "Explore Subscription Plans"}
            </Button>
        </Stack>
      </SectionCard>
    );
  }
  // If there IS a subscription
  return (
    <SectionCard title="Current Subscription" icon={<CreditCardIcon />} contentSx={{ justifyContent: 'space-between' }}>
      <Box>
        <Chip
            icon={statusInfo.icon}
            label={statusInfo.label}
            color={statusInfo.color}
            variant={statusInfo.variant}
            sx={{ 
                fontWeight: 'medium', 
                mb: 2.5, 
                fontSize:'0.9rem', 
                py: 2.25, px:1.25, // Make chip a bit larger
                borderRadius: '10px',
                width: '100%', // Make chip full width for emphasis
                justifyContent: 'flex-start', // Align icon and text left
                '& .MuiChip-label': {pl: 0.5}
            }}
        />
        <List dense disablePadding>
            <ListItem disableGutters sx={{pb: 1.5}}>
            <ListItemIcon sx={{ minWidth: 40, color: theme.palette.text.secondary }}><PremiumIcon fontSize="small"/></ListItemIcon>
            <ListItemText 
                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} 
                primary="Plan Name" 
                secondary={<Typography variant="body1" sx={{ fontWeight: 'medium' }}>{subscription.tier_name || 'N/A'}</Typography>} 
            />
            </ListItem>

            <ListItem disableGutters sx={{pb: 1.5}}>
            <ListItemIcon sx={{ minWidth: 40, color: theme.palette.text.secondary }}><AutorenewIcon fontSize="small"/></ListItemIcon>
            <ListItemText
                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                primary={
                    subscription.status === 'trialing' && subscription.trial_end ? "Trial Ends On" : 
                    (subscription.cancel_at_period_end ? "Subscription Expires On" : "Next Renewal On")
                }
                secondary={
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {formatDate(
                            subscription.status === 'trialing' && subscription.trial_end ? subscription.trial_end : 
                            subscription.current_period_end, 
                            {hour: 'numeric', minute: 'numeric'} // Add time for specific dates
                        )}
                    </Typography>
                }
            />
            </ListItem>

            {subscription.cancel_at_period_end && subscription.status?.toLowerCase() !== 'canceled' && (
            <MuiAlert 
                severity="warning" 
                icon={<EventBusyIcon fontSize="inherit" />} 
                variant="outlined"
                sx={{ 
                    mt:1, mb:2, 
                    borderRadius: '12px',
                    borderColor: theme.palette.warning.light,
                    backgroundColor: alpha(theme.palette.warning.main, 0.05)
                }}
            >
                Your subscription is set to cancel at the end of the current period ({formatDate(subscription.current_period_end)}).
            </MuiAlert>
            )}
            {subscription.status?.toLowerCase() === 'past_due' && (
                 <MuiAlert severity="error" icon={<ErrorOutlineIcon />} sx={{mt:1, mb:2, borderRadius:'12px'}}>
                    There's an issue with your payment. Please update your payment method.
                </MuiAlert>
            )}
        </List>
      </Box>
      <Button
        variant="contained"
        color="primary"
        onClick={onManageSubscription}
        disabled={isLoadingCheckout}
        startIcon={isLoadingCheckout ? <CircularProgress size={20} color="inherit" /> :<ManageAccountsIcon />}
        sx={{ 
            mt: 'auto', // Pushes to bottom
            width: '100%', 
            py: 1.5, 
            textTransform: 'none', 
            fontWeight: 'bold', 
            fontSize: '1rem',
            borderRadius: '12px',
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
             '&:hover': {
                boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                transform: 'translateY(-1px)'
            }
        }}
      >
        Manage Subscription & Billing
      </Button>
    </SectionCard>
  );
};

export default CurrentSubscriptionDetails;